export const DEFAULT_ORG_MAIN_TRAX_STATEMENT_ORDER = [
  'purpose',
  'problemSolved',
  'why',
  'promise',
  'vision',
  'mission',
  'coreValues',
  'cultureStatement',
  'valueCreated',
] as const;

export type OrgMainTraxStatementKey = typeof DEFAULT_ORG_MAIN_TRAX_STATEMENT_ORDER[number];

export interface OrgStatementRecord {
  id?: number;
  organization_id?: number;
  slug: string;
  title: string;
  body?: string | null;
  is_hidden?: number | boolean;
  sort_order?: number;
  is_system?: number | boolean;
  deleted_at?: string | null;
}

export interface OrgStatementViewModel extends OrgStatementRecord {
  key: string;
  label: string;
  content: string;
  hidden: boolean;
  source: 'legacy' | 'table';
}

export const ORG_STATEMENT_HIDE_FIELD: Record<OrgMainTraxStatementKey, string> = {
  purpose: 'hide_purpose_in_main_trax',
  problemSolved: 'hide_problem_solved_in_main_trax',
  why: 'hide_why_in_main_trax',
  promise: 'hide_promise_in_main_trax',
  vision: 'hide_vision_in_main_trax',
  mission: 'hide_mission_in_main_trax',
  coreValues: 'hide_core_values_in_main_trax',
  cultureStatement: 'hide_culture_statement_in_main_trax',
  valueCreated: 'hide_value_created_in_main_trax',
};

export const ORG_STATEMENT_LABELS: Record<OrgMainTraxStatementKey, string> = {
  purpose: 'Purpose',
  problemSolved: 'Problem Solved',
  why: 'Why',
  promise: 'Promise',
  vision: 'Vision',
  mission: 'Mission',
  coreValues: 'Core Values',
  cultureStatement: 'Culture Statement',
  valueCreated: 'Value Created',
};

const DEFAULT_SET = new Set<string>(DEFAULT_ORG_MAIN_TRAX_STATEMENT_ORDER);

function asStringArray(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.map((item) => String(item));
  }
  if (typeof raw === 'string' && raw.trim() !== '') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item));
      }
    } catch {
      return [];
    }
  }
  return [];
}

function isHidden(value: number | boolean | null | undefined): boolean {
  return value === 1 || value === true;
}

/** Normalize saved order; append any new default keys the org has not ordered yet. */
export function parseOrgMainTraxStatementOrder(raw: unknown): OrgMainTraxStatementKey[] {
  const seen = new Set<string>();
  const ordered: OrgMainTraxStatementKey[] = [];

  asStringArray(raw).forEach((key) => {
    if (DEFAULT_SET.has(key) && !seen.has(key)) {
      seen.add(key);
      ordered.push(key as OrgMainTraxStatementKey);
    }
  });

  DEFAULT_ORG_MAIN_TRAX_STATEMENT_ORDER.forEach((key) => {
    if (!seen.has(key)) {
      ordered.push(key);
    }
  });

  return ordered;
}

export function resolveOrgMainTraxStatementOrder(organization: any): OrgMainTraxStatementKey[] {
  return parseOrgMainTraxStatementOrder(organization?.main_trax_statement_order);
}

export function buildOrgMainTraxStatementMap(organization: any): Record<string, string> {
  const labelMap: Record<string, string> = {};
  const order = resolveOrgMainTraxStatementOrder(organization);

  order.forEach((key) => {
    labelMap[key] = ORG_STATEMENT_LABELS[key];
  });

  return labelMap;
}

export function normalizeOrgMainTraxStatements(
  organization: any,
  rows: OrgStatementRecord[] = []
): OrgStatementViewModel[] {
  const statements: OrgStatementViewModel[] = [];
  const seen = new Set<string>();
  const tableRows = (rows || []).filter((row) => row?.slug && !row.deleted_at);

  const pushLegacy = (key: OrgMainTraxStatementKey) => {
    const slug = key;
    if (seen.has(slug)) return;
    seen.add(slug);

    const legacyValue = organization?.[key] ?? '';
    const hideField = ORG_STATEMENT_HIDE_FIELD[key];
    const hidden = isHidden(organization?.[hideField]);

    statements.push({
      key: slug,
      slug,
      title: ORG_STATEMENT_LABELS[key],
      label: ORG_STATEMENT_LABELS[key],
      body: legacyValue,
      content: legacyValue,
      hidden,
      is_hidden: hidden,
      is_system: true,
      source: 'legacy',
    });
  };

  const order = resolveOrgMainTraxStatementOrder(organization);
  const orderIndexBySlug = new Map<string, number>();
  order.forEach((key, index) => {
    orderIndexBySlug.set(key, index);
  });

  const getSortRank = (statement: OrgStatementViewModel | OrgStatementRecord) => {
    if (typeof statement.sort_order === 'number') {
      return statement.sort_order;
    }

    const slug = statement.slug;
    const index = orderIndexBySlug.get(slug);
    return index === undefined ? Number.MAX_SAFE_INTEGER : index;
  };

  order.forEach((key) => {
    const matchingRow = tableRows.find((row) => row.slug === key);
    if (matchingRow) {
      seen.add(key);
      statements.push({
        ...matchingRow,
        key: matchingRow.slug,
        title: matchingRow.title || ORG_STATEMENT_LABELS[key] || matchingRow.slug,
        label: matchingRow.title || ORG_STATEMENT_LABELS[key] || matchingRow.slug,
        content: matchingRow.body || '',
        hidden: isHidden(matchingRow.is_hidden),
        is_hidden: isHidden(matchingRow.is_hidden),
        is_system: Boolean(matchingRow.is_system),
        source: 'table',
      });
      return;
    }

    pushLegacy(key);
  });

  tableRows.forEach((row) => {
    if (!row?.slug || seen.has(row.slug)) return;

    seen.add(row.slug);
    statements.push({
      ...row,
      key: row.slug,
      title: row.title ?? row.slug,
      label: row.title ?? row.slug,
      content: row.body || '',
      hidden: isHidden(row.is_hidden),
      is_hidden: isHidden(row.is_hidden),
      is_system: Boolean(row.is_system),
      source: 'table',
    });
  });

  return statements.sort((a, b) => getSortRank(a) - getSortRank(b));
}

export function resolveOrgMainTraxStatements(organization: any, rows: OrgStatementRecord[] = []): OrgStatementViewModel[] {
  return normalizeOrgMainTraxStatements(organization, rows);
}
